import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { IoArrowBackCircle } from "react-icons/io5";
import "./DriverChecklist.css";

const DriverChecklist = () => {
  const [formData, setFormData] = useState({
    driverName: "",
    badgeNumber: "",
    fleetNumber: "",
    kmReading: "",
    location: "Duqm",
    date: "",
    checklist: Array(27).fill(false),
    defects: "",
    actionTaken: "",
    driverSignature: "",
    jmSignature: "",
    technicianSignature: "",
    checkliststatus: "",
  });

  const [language, setLanguage] = useState("en");
  const [srNo, setSrNo] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();
  const [drivers, setDrivers] = useState([]);
  const [jms, setJms] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState("");

  // Add new state for tracking changes
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);

  // Add deep comparison function
  const hasFormChanged = (current, initial) => {
    if (!initial) return false;
    return JSON.stringify(current) !== JSON.stringify(initial);
  };

  // treat "NULL"/"null"/empty as null
  const normalizeVal = (v) => {
    if (v === undefined || v === null) return null;
    if (typeof v === "string") {
      const t = v.trim();
      if (t === "" || t.toLowerCase() === "null") return null;
      return t;
    }
    return v;
  };

  useEffect(() => {
    if (id) {
      const fetchChecklist = async () => {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.post(
            `${process.env.REACT_APP_BASE_API_URL}/checklist`,
            { id: decodeURIComponent(id) },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          let data = response.data;
          if (data && data.data) data = data.data;
          data = data || {};

          // normalize keys to lowercase and values
          const norm = {};
          Object.keys(data).forEach((k) => {
            norm[k.toLowerCase()] = normalizeVal(data[k]);
          });

          const checklist = Array(27)
            .fill(null)
            .map((_, index) =>
              norm[`checklistitem${index + 1}`] === "Y" ? "Y" : "N"
            );

          const updatedFormData = {
            driverName: norm.drivername || "",
            badgeNumber: norm.badgenumber || "",
            fleetNumber: norm.fleetnumber || "",
            kmReading: norm.kmreading || "",
            location: norm.location || "Duqm",
            date: norm.checklistdate || norm.date || "",
            checklist,
            defects: norm.defects || "",
            actionTaken: norm.actiontaken || "",
            driverSignature: norm.driversignature || "",
            jmSignature: norm.jmsignature || "",
            technicianSignature: norm.techniciansignature || "",
          };

          setFormData(updatedFormData);
          setInitialFormData(updatedFormData);
          setSrNo(norm.sr_no || "");
        } catch (error) {
          console.error("Error fetching checklist data:", error);
        }
      };
      fetchChecklist();
    } else {
      const today = new Date().toISOString().split("T")[0];
      // Initialize with all empty values except date and checklist
      const newFormData = {
        driverName: "",
        badgeNumber: "",
        fleetNumber: "",
        kmReading: "",
        location: "Duqm", // Set default location to Duqm
        date: today,
        checklist: Array(27).fill("N"),
        defects: "",
        actionTaken: "",
        driverSignature: "",
        jmSignature: "",
        technicianSignature: "",
      };
      setFormData(newFormData);
      setInitialFormData(newFormData);
    }
  }, [id]); // Remove formData from dependencies

  useEffect(() => {
    const fetchDriverDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        const userRole = localStorage.getItem("userrole");
        const driverName = localStorage.getItem("driverName");
        const driverId = localStorage.getItem("driverId");

        if (userRole === "driver" && driverName && driverId) {
          const response = await axios.get(
            `${
              process.env.REACT_APP_BASE_API_URL 
            }/driver-details/${encodeURIComponent(
              driverName
            )}/${encodeURIComponent(driverId)}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          // support: response.data can be array, object, or { data: ... }
          let rec = response.data;
          if (Array.isArray(rec)) rec = rec[0] || {};
          if (rec && rec.data) rec = rec.data;

          // normalize: keys to lowercase, values (treat "NULL" as null)
          const norm = {};
          Object.keys(rec || {}).forEach((k) => {
            norm[k.toLowerCase()] = normalizeVal(rec[k]);
          });

           setFormData((prev) => ({
             ...prev,
            driverName: norm.drivername || "",
            badgeNumber: norm.badgenumber || "",
            fleetNumber: norm.fleetnumber || "",
            driverSignature: norm.driversignature || "",
           }));
         } else {
           const response = await axios.get(
             `${process.env.REACT_APP_BASE_API_URL}/drivers`,
             {
               headers: { Authorization: `Bearer ${token}` },
             }
           );
           setDrivers(response.data);
         }
       } catch (error) {
         console.error("Error fetching driver details:", error);
       }
     };

     fetchDriverDetails();
   }, []);

  useEffect(() => {
    const fetchJms = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/jms`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setJms(response.data);
      } catch (error) {
        console.error("Error fetching JMs:", error);
      }
    };

    const fetchTechnicians = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/technicians`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setTechnicians(response.data);
      } catch (error) {
        console.error("Error fetching Technicians:", error);
      }
    };

    fetchJms();
    fetchTechnicians();
  }, []);

  const checklistItems = [
    { en: "Brakes", ar: "الفرامل" },
    { en: "First Aid Box", ar: "صندوق الاستعاقات الأولية" },
    { en: "Fire Extinguisher", ar: "طغاية الحريق" },
    { en: "Jack, Tools & Triangle", ar: "مثلث التحدير والادوات" },
    { en: "Vehicle Registration Papers", ar: "الملكية" },
    { en: "RAS / Safety Stickers", ar: "نظام الفحص للدوري (رأس)" },
    { en: "Trailer Head Board/Bed/Jack", ar: "سرير الشفيرة لزيج الرأس الجلد" },
    { en: "Vehicle Clean", ar: "نطاقه المركبة" },
    { en: "Wheel Chocks", ar: "حواجز العجلات" },
    { en: "Body Damage", ar: "الهيكل" },
    { en: "Tyres", ar: "الإطارات" },
    { en: "Load Restraint", ar: "تأكد من ربط الأمنعة" },
    { en: "Battery", ar: "البطارية" },
    { en: "Oil (Level)", ar: "مستوى الزيت" },
    { en: "Water (Level)", ar: "قياس الماء" },

    { en: "Steering", ar: "المقود" },
    { en: "Wipers/Windscreen", ar: "المساحات الشافعات" },
    { en: "Lights/Indicators", ar: "الأنواب الإشابات (الكهرباء)" },
    { en: "Mirrors", ar: "المريا" },
    { en: "Speedometer & Speed Restrictor", ar: "عدد السرعة ومقيد السرعة" },
    { en: "Horn (Front & Rev)", ar: "الهون" },
    { en: "Fuel", ar: "أبو قود" },
    { en: "Seat Belt", ar: "حرام الأمان" },
    { en: "Radio/Tape", ar: "الراديو" },
    { en: "A/C in Vehicle", ar: "المكيف" },
    { en: "Fuel Anti Theft Lock", ar: "فقل خزان" },
    {
      en: "5th Wheel/Kingpin condition & Lock",
      ar: "العجلة الخمسة. الحالة والعقل",
    },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newFormData = { ...formData };

    if (type === "checkbox") {
      const index = parseInt(name.split("-")[1], 10);
      newFormData.checklist = newFormData.checklist.map((item, i) =>
        i === index ? checked : item
      );
    } else {
      newFormData[name] = value;
    }

    setFormData(newFormData);
    setIsFormChanged(hasFormChanged(newFormData, initialFormData));
  };

  const handleDriverChange = (e) => {
    const selectedDriver = drivers.find(
      (driver) => driver.driverName === e.target.value
    );
    let newFormData;
    if (selectedDriver) {
      newFormData = {
        ...formData,
        driverName: selectedDriver.driverName,
        badgeNumber: selectedDriver.badgeNumber || "",
        fleetNumber: selectedDriver.fleetNumber || "",
        driverSignature: selectedDriver.driverSignature || "",
        USER_ID: selectedDriver.USER_ID || "",
      };
    } else {
      newFormData = {
        ...formData,
        driverName: e.target.value,
        badgeNumber: "",
        fleetNumber: "",
        driverSignature: "",
        userId: "",
      };
    }
    setFormData(newFormData);
    setIsFormChanged(hasFormChanged(newFormData, initialFormData));
  };

  const handleChecklistToggle = (index) => {
    const newFormData = {
      ...formData,
      checklist: formData.checklist.map((item, i) =>
        i === index ? (item === "Y" ? "N" : "Y") : item
      ),
    };
    setFormData(newFormData);
    setIsFormChanged(hasFormChanged(newFormData, initialFormData));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Count the number of checkboxes marked with "✔" (Y)
    const checkedCount = formData.checklist.filter(
      (item) => item === "Y"
    ).length;

    // Determine checklist status
    const checkliststatus = checkedCount === 27 ? "pass" : "fail";

    // Show confirmation alert with the count of "✔" checkboxes
    const confirmSubmit = window.confirm(
      `You have marked ${checkedCount} items with ✔. The checklist status will be ${checkliststatus.toUpperCase()}. Do you want to submit?`
    );
    if (!confirmSubmit) {
      return;
    }

    const requiredFields = [
      { name: "driverName", label: "Driver's Name" },
      { name: "badgeNumber", label: "Badge #" },
      { name: "fleetNumber", label: "Fleet No" },
      { name: "date", label: "Date" },
      { name: "kmReading", label: "KM's Reading" },
      { name: "location", label: "Location" },
    ];

    for (const field of requiredFields) {
      if (!formData[field.name]) {
        alert(`Please fill in the required field: ${field.label}`);
        document
          .getElementsByName(field.name)[0]
          .scrollIntoView({ behavior: "smooth" });
        return;
      }
    }

    const payload = {
      srNo,
      driverName: formData.driverName,
      badgeNumber: formData.badgeNumber,
      fleetNumber: formData.fleetNumber,
      kmReading: formData.kmReading,
      location: formData.location,
      date: formData.date,
      ...formData.checklist.reduce(
        (acc, value, index) => ({
          ...acc,
          [`checklistItem${index + 1}`]: value,
        }),
        {}
      ),
      defects: formData.defects,
      actionTaken: formData.actionTaken,
      driverSignature: formData.driverSignature,
      jmSignature: formData.jmSignature,
      technicianSignature: formData.technicianSignature,
      checkliststatus,
    };

    if (id) {
      const userRole = localStorage.getItem("userrole");
      if (userRole !== "driver" && formData.userId) {
        payload.USER_ID = formData.USER_ID;
      }
    } else {
      const userRole = localStorage.getItem("userrole");
      if (userRole === "driver") {
        const userId = localStorage.getItem("driverId");
        payload.USER_ID = userId;
      }
    }

    try {
      const token = localStorage.getItem("token");
      if (id) {
        await axios.put(
          `${process.env.REACT_APP_BASE_API_URL}/checklist/${encodeURIComponent(
            id
          )}`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log("Data updated successfully");
        setSuccessMessage("Checklist record successfully updated!");
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_API_URL}/save-checklist`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log("Data submitted successfully:", response.data);
        setSrNo(response.data.srNo);
        setSuccessMessage(
          `Checklist record successfully added! Generated SR_NO: ${response.data.srNo}`
        );
      }

      window.scrollTo({ top: 0, behavior: "smooth" });

      setTimeout(() => {
        setSuccessMessage("");
        navigate("/driver-checklists/");
      }, 2000);
    } catch (error) {
      console.error("Error submitting data:", error);
    }
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "en" ? "ar" : "en"));
  };

  const handlePopupOpen = (type, event) => {
    event.preventDefault();
    setPopupType(type);
    setShowPopup(true);
  };

  const handlePopupClose = () => {
    setShowPopup(false);
    setPopupType("");
  };

  const handleSelectSignature = (type, selectedName, selectedSignature) => {
    let newFormData;
    if (type === "jm") {
      newFormData = {
        ...formData,
        jmSignature: selectedSignature,
      };
    } else if (type === "technician") {
      newFormData = {
        ...formData,
        technicianSignature: selectedSignature,
      };
    }
    setFormData(newFormData);
    setIsFormChanged(hasFormChanged(newFormData, initialFormData));
    handlePopupClose();
  };
  console.log("Data updated successfully", jms);
  return (
    <div className="driver-checklist-page">
      {successMessage && (
        <div className="success-message">
          <h3>{successMessage}</h3>
        </div>
      )}

      {/* Checklist No Section */}
      {srNo && (
        <div className="checklist-no-container">
          <div className="checklist-no-label">
            {language === "en" ? "Checklist No:" : "رقم قائمة التحقق:"}
          </div>
          <div className="checklist-no-value">{srNo}</div>
          <div className="checklist-no-divider"></div>
        </div>
      )}

      <div className="header-buttons">
        <button className="language-toggle-button" onClick={toggleLanguage}>
          {language === "en" ? "Switch to Arabic" : "Switch to English"}
        </button>
        <button
          className="react-icons-back-button"
          onClick={() => navigate("/driver-checklists/")}
        >
          <IoArrowBackCircle style={{ fontSize: "18px" }} />
        </button>
        <button
          className="cross-back-button"
          onClick={() => navigate("/driver-checklists/")}
        >
          ✕
        </button>
      </div>
      <div className="driver-checklist-header">
        <img
          src="/headerLogo.jpg"
          alt="Al Madina Logo"
          className="driver-checklist-logo"
        />
        <div className="driver-checklist-info-box">
          {/* Row 1 */}
          <div className="info-row">
            <div className="input-group">
              <label>
                {language === "en" ? "Driver's Name:" : "اسم السائق:"}
                <span className="required">*</span>
              </label>
              <input
                list="drivers-list"
                type="text"
                name="driverName"
                value={formData.driverName}
                onChange={handleDriverChange}
                dir={language === "en" ? "ltr" : "rtl"}
                required
              />
            </div>

            <div className="input-group">
              <label>
                {language === "en" ? "Badge #:" : "رقم الشارة:"}
                <span className="required">*</span>
              </label>
              <input
                type="text"
                name="badgeNumber"
                value={formData.badgeNumber}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="info-row">
            <div className="input-group">
              <label>
                {language === "en" ? "Fleet No:" : "رقم الأسطول:"}
                <span className="required">*</span>
              </label>
              <input
                type="text"
                name="fleetNumber"
                value={formData.fleetNumber}
                onChange={handleChange}
                required
              />
            </div>
            <div className="input-group">
              <label>
                {language === "en" ? "KM's Reading:" : "قراءة الكيلومترات:"}
                <span className="required">*</span>
              </label>
              <input
                type="text"
                name="kmReading"
                value={formData.kmReading}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Row 3 */}
          <div className="info-row">
            <div className="input-group">
              <label>
                {language === "en" ? "Location:" : "الموقع:"}
                <span className="required">*</span>
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
              />
            </div>
            <div className="input-group">
              <label>
                {language === "en" ? "Date:" : "التاريخ:"}
                <span className="required">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <datalist id="drivers-list">
            {drivers.map((driver) => (
              <option key={driver.driverName} value={driver.driverName}>
                {driver.driverName}
              </option>
            ))}
          </datalist>
        </div>
      </div>
      {/* Main Checklist Grid */}
      <form onSubmit={handleSubmit} id="driver-checklist-form">
        <div className="driver-checklist-grid">
          {[...Array(27)].map((_, index) => (
            <div key={index} className="driver-checklist-item">
              <span className="checklist-number">{index + 1}</span>
              <img
                src={`${process.env.PUBLIC_URL}/${index + 1}.jpg`}
                alt={`Checklist ${index + 1}`}
                className="driver-checklist-image"
                style={{ width: "46px", height: "46px" }}
              />
              <div className="driver-checklist-label">
                <span>{checklistItems[index][language]}</span>
              </div>
              <button
                type="button"
                className={`checklist-toggle ${
                  formData.checklist[index] === "Y" ? "checked" : "unchecked"
                }`}
                onClick={() => handleChecklistToggle(index)}
              >
                {formData.checklist[index] === "Y" ? "✔" : "X"}
              </button>
            </div>
          ))}
        </div>

        {/* Footer Section */}
        <div className="driver-checklist-footer">
          <div className="driver-checklist-defects">
            <label>
              {language === "en" ? "Details of Defects:" : "تفاصيل العيوب:"}
            </label>
            <textarea
              name="defects"
              value={formData.defects}
              onChange={handleChange}
              dir={language === "en" ? "ltr" : "rtl"}
            />
          </div>
          <div className="driver-checklist-action">
            <label>
              {language === "en"
                ? "Action Taken on Reported Defects:"
                : "الإجراءات المتخذة على العيوب المبلغ عنها:"}
            </label>
            <textarea
              name="actionTaken"
              value={formData.actionTaken}
              onChange={handleChange}
              dir={language === "en" ? "ltr" : "rtl"}
            />
          </div>
        </div>

        {/* Hide Signatures */}
        <div className="driver-checklist-signatures">
          <div className="driver-checklist-signature">
            <label>
              {language === "en" ? "Driver's Signature:" : "توقيع السائق:"}
            </label>
            {formData.driverSignature ? (
              <img
                src={formData.driverSignature}
                alt="Driver Signature"
                className="driver-signature-preview"
              />
            ) : (
              <span>
                {language === "en" ? "No signature available" : "لا يوجد توقيع"}
              </span>
            )}
          </div>
          <div className="driver-checklist-signature">
            <label>
              {language === "en" ? "JM's Signature:" : "توقيع المدير:"}
            </label>
            {formData.jmSignature ? (
              <img
                src={formData.jmSignature}
                alt="JM Signature"
                className="signature-preview"
              />
            ) : (
              <span>No signature available</span>
            )}
            <button
              className="select-jm-button"
              onClick={(e) => handlePopupOpen("jm", e)}
              disabled={!id}
            >
              Select JM
            </button>
          </div>
          <div className="driver-checklist-signature">
            <label>
              {language === "en" ? "Technician's Signature:" : "توقيع الفني:"}
            </label>
            {formData.technicianSignature ? (
              <img
                src={formData.technicianSignature}
                alt="Technician Signature"
                className="signature-preview"
              />
            ) : (
              <span>No signature available</span>
            )}
            <button
              className="select-technician-button"
              onClick={(e) => handlePopupOpen("technician", e)}
              disabled={!id}
            >
              Select Technician
            </button>
          </div>
        </div>
      </form>

      {showPopup && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="popup-close-icon" onClick={handlePopupClose}>
              ✕ {/* Small "X" symbol for closing the popup */}
            </button>
            <h3>Select {popupType === "jm" ? "JM" : "Technician"} Signature</h3>
            <div className="popup-list-container">
              <ul>
                {(popupType === "jm" ? jms : technicians).map((item) => (
                  <li
                    key={
                      popupType === "jm"
                        ? item.jmName || item.id
                        : item.technicianName || item.id
                    }
                  >
                    <div className="popup-item">
                      <span>
                        {popupType === "jm"
                          ? item.jmName || "No Name Available"
                          : item.technicianName || "No Name Available"}
                      </span>
                      {popupType === "jm" && item.jmSignature && (
                        <img
                          src={item.jmSignature}
                          alt="JM Signature"
                          className="popup-signature-preview"
                        />
                      )}
                      {popupType === "technician" &&
                        item.technicianSignature && (
                          <img
                            src={item.technicianSignature}
                            alt="Technician Signature"
                            className="popup-signature-preview"
                          />
                        )}
                      <button
                        className="select-button"
                        onClick={() =>
                          handleSelectSignature(
                            popupType,
                            popupType === "jm"
                              ? item.jmName || "Unknown"
                              : item.technicianName || "Unknown",
                            popupType === "jm"
                              ? item.jmSignature
                              : item.technicianSignature
                          )
                        }
                      >
                        Select
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      <div>
        <button
          type="submit"
          className="driver-checklist-submit"
          form="driver-checklist-form"
          disabled={!isFormChanged}
          style={{ opacity: isFormChanged ? 1 : 0.5 }}
        >
          {language === "en" ? "Submit" : "إرسال"}
        </button>
      </div>
    </div>
  );
};

export default DriverChecklist;
